// src/pages/JoinFriend.tsx
// Handles invite links: /join/:userId
// FIXED: Better error handling and query syntax
'use client';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSupabase, currentUser } from "@/lib/supabase";
import { Loader2, UserPlus, Check, X, LogIn } from 'lucide-react';

// Helper to call edge functions
async function callEdgeFunction(functionName: string, body: object) {
  const supabase = getSupabase();
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(body),
    }
  );

  const data = await response.json();
  
  if (!response.ok) {
    // Throw the actual error message from the edge function
    throw new Error(data.error || `Request failed with status ${response.status}`);
  }

  return data;
}

export default function JoinFriend() {
  const { userId: friendId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState<'loading' | 'not-logged-in' | 'processing' | 'success' | 'error' | 'already-friends' | 'request-sent'>('loading');
  const [friendName, setFriendName] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    handleJoin();
  }, [friendId]);

  const handleJoin = async () => {
    if (!friendId) {
      setStatus('error');
      setErrorMessage('Invalid invite link');
      return;
    }

    const supabase = getSupabase();
    
    // Check if user is logged in
    const user = await currentUser();
    
    if (!user) {
      // Not logged in - save the invite to localStorage and show login options
      localStorage.setItem('pendingFriendInvite', friendId);
      
      // Try to get friend's name for the UI
      const { data: friendData } = await supabase
        .from('clients')
        .select('name')
        .eq('user_id', friendId)
        .maybeSingle();

      if (friendData) {
        setFriendName(friendData.name);
      }
      
      setStatus('not-logged-in');
      return;
    }

    // Can't friend yourself
    if (user.id === friendId) {
      setStatus('error');
      setErrorMessage("You can't add yourself as a friend!");
      return;
    }

    setStatus('processing');

    // Get friend's name for UI
    const { data: friendData } = await supabase
      .from('clients')
      .select('name')
      .eq('user_id', friendId)
      .maybeSingle();

    if (friendData) {
      setFriendName(friendData.name);
    }

    try {
      // FIXED: Use two separate queries instead of complex .or() syntax
      // Check if I sent them a request
      const { data: iSentRequest } = await supabase
        .from('friendships')
        .select('id, status')
        .eq('user_id', user.id)
        .eq('friend_id', friendId)
        .maybeSingle();

      // Check if they sent me a request
      const { data: theySentRequest } = await supabase
        .from('friendships')
        .select('id, status')
        .eq('user_id', friendId)
        .eq('friend_id', user.id)
        .maybeSingle();

      console.log('Friendship check:', { iSentRequest, theySentRequest });

      // Handle case: They sent me a request - accept it
      if (theySentRequest) {
        if (theySentRequest.status === 'accepted') {
          setStatus('already-friends');
          return;
        }
        
        if (theySentRequest.status === 'pending') {
          // Accept their request
          console.log('Accepting friend request:', theySentRequest.id);
          try {
            await callEdgeFunction('accept-friend-request', { 
              friendship_id: theySentRequest.id 
            });
            setStatus('success');
            return;
          } catch (err: any) {
            console.error('Error accepting friend request:', err);
            setStatus('error');
            setErrorMessage(err.message || 'Failed to accept friend request');
            return;
          }
        }
      }

      // Handle case: I already sent them a request
      if (iSentRequest) {
        if (iSentRequest.status === 'accepted') {
          setStatus('already-friends');
          return;
        }
        
        if (iSentRequest.status === 'pending') {
          setStatus('request-sent');
          return;
        }
      }

      // No existing friendship - send a new friend request
      console.log('Sending new friend request to:', friendId);
      await callEdgeFunction('send-friend-request', { friend_id: friendId });
      setStatus('success');
      
    } catch (err: any) {
      console.error('Error in friend request flow:', err);
      setStatus('error');
      setErrorMessage(err.message || 'Something went wrong. Please try again.');
    }
  };

  const goToLogin = () => {
    navigate('/login');
  };

  const goToSignup = () => {
    navigate('/signup');
  };

  const goToLeaderboard = () => {
    navigate('/leaderboard');
  };

  const retry = () => {
    setStatus('loading');
    setErrorMessage('');
    handleJoin();
  };

  return (
    <div className="min-h-screen bg-[#ffffff] flex items-center justify-center p-4">
      <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-8 max-w-md w-full text-center">
        
        {/* Loading */}
        {status === 'loading' && (
          <div className="py-8">
            <Loader2 className="w-12 h-12 animate-spin text-green-400 mx-auto mb-4" />
            <p className="text-gray-500">Processing invite...</p>
          </div>
        )}

        {/* Not Logged In */}
        {status === 'not-logged-in' && (
          <div className="py-4">
            <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <LogIn className="w-8 h-8 text-blue-400" />
            </div>
            <h1 className="text-2xl font-bold text-[#0f172a] mb-2">Join Input</h1>
            <p className="text-gray-500 mb-6">
              {friendName ? `${friendName} invited you` : 'You\'ve been invited'} to compete on Input!
            </p>
            <div className="space-y-3">
              <button
                onClick={goToSignup}
                className="w-full px-6 py-3 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-xl transition"
              >
                Create Account
              </button>
              <button
                onClick={goToLogin}
                className="w-full px-6 py-3 bg-[#f1f5f9] hover:bg-[#f1f5f9] text-[#0f172a] font-medium rounded-xl transition"
              >
                I already have an account
              </button>
            </div>
          </div>
        )}

        {/* Processing */}
        {status === 'processing' && (
          <div className="py-8">
            <Loader2 className="w-12 h-12 animate-spin text-green-400 mx-auto mb-4" />
            <p className="text-gray-500">Sending friend request{friendName ? ` to ${friendName}` : ''}...</p>
          </div>
        )}

        {/* Success - Request Sent/Accepted */}
        {status === 'success' && (
          <div className="py-4">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-[#0f172a] mb-2">
              {friendName ? `Connected with ${friendName}!` : 'Success!'}
            </h1>
            <p className="text-gray-500 mb-6">
              You're now friends and can compete together on the leaderboard!
            </p>
            <button
              onClick={goToLeaderboard}
              className="w-full px-6 py-3 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-xl transition"
            >
              Go to Leaderboard
            </button>
          </div>
        )}

        {/* Request Already Sent (Pending) */}
        {status === 'request-sent' && (
          <div className="py-4">
            <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserPlus className="w-8 h-8 text-yellow-400" />
            </div>
            <h1 className="text-2xl font-bold text-[#0f172a] mb-2">Request Pending</h1>
            <p className="text-gray-500 mb-6">
              You've already sent {friendName ? `${friendName} ` : ''}a friend request. Waiting for them to accept!
            </p>
            <button
              onClick={goToLeaderboard}
              className="w-full px-6 py-3 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-xl transition"
            >
              Go to Leaderboard
            </button>
          </div>
        )}

        {/* Already Friends */}
        {status === 'already-friends' && (
          <div className="py-4">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserPlus className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-[#0f172a] mb-2">Already Friends!</h1>
            <p className="text-gray-500 mb-6">
              You're already friends with {friendName || 'this user'}. Time to compete!
            </p>
            <button
              onClick={goToLeaderboard}
              className="w-full px-6 py-3 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-xl transition"
            >
              Go to Leaderboard
            </button>
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div className="py-4">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-[#0f172a] mb-2">Oops!</h1>
            <p className="text-gray-500 mb-2">{errorMessage}</p>
            
            {/* Show debug info in development */}
            {import.meta.env.DEV && (
              <p className="text-xs text-gray-600 mb-4 font-mono">
                Friend ID: {friendId}
              </p>
            )}
            
            <div className="space-y-3">
              <button
                onClick={retry}
                className="w-full px-6 py-3 bg-green-500 hover:bg-green-400 text-[#0f172a] font-bold rounded-xl transition"
              >
                Try Again
              </button>
              <button
                onClick={goToLeaderboard}
                className="w-full px-6 py-3 bg-[#f1f5f9] hover:bg-[#f1f5f9] text-[#0f172a] font-medium rounded-xl transition"
              >
                Go to Leaderboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
